import { FreeMarketLeadsApi } from '@services/api/free-market-leads'
import classNames from 'classnames'
import { ProgressBar } from '@components/pages/mercado-livre-de-energia/section-form-banner/free-market-lead-form/multiple-file-upload-container/single-file-upload-with-progress/progress-bar'
import { HTMLAttributes, useCallback, useEffect, useState } from 'react'
import { FileError } from 'react-dropzone'
import { IoIosCloseCircle, IoIosDocument } from 'react-icons/io'
import { RiErrorWarningLine } from 'react-icons/ri'
import ClipLoader from 'react-spinners/ClipLoader'

export interface Props extends HTMLAttributes<HTMLDivElement> {
  file: File
  onDelete: (fileName: string) => void
  onUpload: (file: File, url: string) => void
  errors?: FileError[]
}

export const SingleFileUploadWithProgress = ({
  file,
  onUpload,
  onDelete,
  errors,
  ...rest
}: Props) => {
  const [uploadLoading, setUploadLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [size, setSize] = useState(0)
  const uploadFile = useCallback(
    async (
      file: File,
      onProgress: (percentage: number) => void,
      onSizeLoading: (size: number) => void
    ) => {
      try {
        setUploadLoading(true)
        const formdata = new FormData()
        formdata.append('image', file)
        formdata.append('name', file.name)
        const { data } = await FreeMarketLeadsApi.upload(
          formdata,
          onProgress,
          onSizeLoading
        )

        onUpload(file, data.file.url)
      } catch (error) {
        console.log({ error })
      } finally {
        setUploadLoading(false)
      }
    },
    []
  )

  const bytesToSize = (bytes: number): string => {
    const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0'
    const i: number = parseInt(
      Math.floor(Math.log(bytes) / Math.log(1024)).toString()
    )
    if (i === 0) return `${bytes} ${sizes[i]}`
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
  }

  useEffect(() => {
    if (errors.length === 0) {
      uploadFile(file, setProgress, setSize)
    }
  }, [])

  return (
    <div
      className={classNames(
        'flex flex-col gap-y-2 font-workSans',
        rest.className
      )}
      {...rest}
    >
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-11 flex items-center">
          <div>
            {errors.length !== 0 ? (
              <RiErrorWarningLine
                size={40}
                className={classNames('text-gray-300', {
                  'text-red': errors.length !== 0
                })}
              />
            ) : (
              <IoIosDocument
                size={40}
                className={classNames('text-gray-300', {
                  'text-red': errors.length !== 0
                })}
              />
            )}
          </div>
          <div className="w-full ml-4">
            {uploadLoading ? (
              <div className="w-full">
                <ProgressBar progress={progress} height="h-2" errors={errors} />
              </div>
            ) : (
              <p
                className={classNames('font-light text-v2-sonicSilver', {
                  'text-red': errors.length !== 0
                })}
              >
                {file.name}
              </p>
            )}
            <p className="text-gray-300 font-light">{bytesToSize(size)}</p>
          </div>
        </div>
        <div className="col-span-1">
          {uploadLoading ? (
            <ClipLoader color="#b3b3b3" loading={uploadLoading} size={20} />
          ) : (
            <IoIosCloseCircle
              size={20}
              className={classNames('text-gray-300 cursor-pointer', {
                'text-red': errors.length !== 0
              })}
              onClick={() => onDelete(file.name)}
            />
          )}
        </div>
      </div>
    </div>
  )
}
